/**
 * Custom hook for handling building placement on tile click.
 * Connects tile clicks to building placement when in build mode.
 */

import { useEffect } from 'react';
import { useBuildModeStore } from '@shared/stores/buildModeStore';
import { BuildingType } from '@shared/constants/buildings';

interface UseBuildingPlacementClickOptions {
  scene: unknown | null;
}

/**
 * Hook for handling building placement on tile click.
 */
export function useBuildingPlacementClick({ scene }: UseBuildingPlacementClickOptions) {
  const { isBuildModeActive, selectedBuildingType } = useBuildModeStore();

  useEffect(() => {
    if (!scene || typeof scene !== 'object' || scene === null) return;

    const sceneWithMethods = scene as {
      getTileInteractionController?: () => {
        setOnTileClick?: (
          callback: (tile: { x: number; y: number }, shiftKey: boolean) => void
        ) => void;
      } | null;
      getBuildingPlacementController?: () => {
        placeBuilding?: (buildingType: BuildingType, tileX: number, tileY: number) => boolean;
        queueBuilding?: (buildingType: BuildingType, tileX: number, tileY: number) => boolean;
        getPreviewState?: () => { isValid: boolean };
      } | null;
    };

    const interactionController = sceneWithMethods.getTileInteractionController?.();
    const buildingController = sceneWithMethods.getBuildingPlacementController?.();

    if (interactionController?.setOnTileClick && buildingController) {
      // Set up tile click handler
      interactionController.setOnTileClick((tile, shiftKey) => {
        // Only place building if in build mode and have a selected building type
        if (isBuildModeActive && selectedBuildingType) {
          const previewState = buildingController.getPreviewState?.();
          // Only place if preview is valid
          if (previewState?.isValid) {
            if (shiftKey) {
              // Shift+click: Add to queue
              buildingController.queueBuilding?.(selectedBuildingType, tile.x, tile.y);
            } else {
              // Regular click: Place immediately
              buildingController.placeBuilding?.(selectedBuildingType, tile.x, tile.y);
            }
            // Keep build mode active so user can place multiple buildings
          }
        }
      });
    }

    // Cleanup: remove click handler when component unmounts or dependencies change
    return () => {
      if (interactionController?.setOnTileClick) {
        interactionController.setOnTileClick(() => {
          // Empty handler for cleanup
        });
      }
    };
  }, [scene, isBuildModeActive, selectedBuildingType]);
}
